import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import EmailCalendarPanel from '@/components/EmailCalendarPanel';
import CreateTaskFromEmailModal from '@/components/CreateTaskFromEmailModal';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import RichTextEditor, { plainTextToHtml } from '@/components/RichTextEditor';
import ComposeModal from '@/components/email/ComposeModal';
import CreateFromEmailModal from '@/components/email/CreateFromEmailModal';
import CreateOppFromEmailModal from '@/components/email/CreateOppFromEmailModal';
import InboxList from '@/components/email/InboxList';
import DraftsList from '@/components/email/DraftsList';
import QuickFilters, { applyQuickFilter } from '@/components/email/QuickFilters';
import ConversationThread from '@/components/email/ConversationThread';
import SnoozeFollowupActions from '@/components/email/SnoozeFollowupActions';

function EmailPage() {
  const { user } = useAuth();
  const [emails, setEmails] = useState([]);
  const [visibleCount, setVisibleCount] = useState(50); // client-side cap to prevent rendering 100s of heavy rows at once
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const [folder, setFolder] = useState('inbox');
  const [search, setSearch] = useState('');
  const [showCompose, setShowCompose] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showCreateOppModal, setShowCreateOppModal] = useState(false);
  const [composeMode, setComposeMode] = useState('new'); // new, reply
  const [message, setMessage] = useState({ type: '', text: '' });
  const [drafts, setDrafts] = useState([]);
  const [draftsCount, setDraftsCount] = useState(0);
  const [selectedDraft, setSelectedDraft] = useState(null);
  
  // Real-time sync and calendar panel states
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [calendarPanelOpen, setCalendarPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('inbox'); // inbox, calendar, tasks
  const syncIntervalRef = useRef(null);

  // Inbox UX (Batch 1): selection for bulk actions, threading toggle, expanded threads
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [threadingEnabled, setThreadingEnabled] = useState(true);
  const [expandedThreads, setExpandedThreads] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Inbox UX (Batch 3): quick filter chip ('all' | 'unread' | 'starred' | 'attachments' | 'leads' | 'today')
  const [quickFilter, setQuickFilter] = useState('all');

  const token = localStorage.getItem('token');

  // Fetch drafts
  const fetchDrafts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/email/drafts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrafts(response.data.drafts || []);
      setDraftsCount(response.data.drafts?.length || 0);
    } catch (error) {
      console.error('Error fetching drafts:', error);
    }
  }, [token]);

  const fetchEmails = useCallback(async () => {
    try {
      // If folder is drafts, show local drafts instead
      if (folder === 'drafts') {
        await fetchDrafts();
        setLoading(false);
        return [];
      }
      
      const params = new URLSearchParams({ folder, limit: '100' });
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/email?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = Array.isArray(response.data?.emails) ? response.data.emails : [];
      setEmails(list);
      setVisibleCount(50); // reset pagination on folder/search change
      setSelectedIds(new Set()); // reset bulk selection on refetch
      setStats(prev => ({ ...prev, unread_count: response.data.unread_count }));
      return list;
    } catch (error) {
      console.error('Error fetching emails:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [folder, search, token, fetchDrafts]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/email/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchEmails();
    fetchStats();
    fetchDrafts(); // Always fetch drafts count for sidebar
  }, [fetchEmails, fetchDrafts]);

  // ─── Deep-link: /email?open=<email_id> opens that specific email ────
  // Used by the Accounts timeline "Email threads" cards to jump straight
  // to a thread. Fetches the email even if it's not in the current folder.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId) return;
    // Consume the param so refresh/back doesn't re-trigger
    axios.get(`${API}/email/${openId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.data) setSelectedEmail(r.data);
      })
      .catch(() => { /* email not found; silently ignore */ })
      .finally(() => {
        // Clear the query param so subsequent navigations don't re-open
        const next = new URLSearchParams(searchParams);
        next.delete('open');
        setSearchParams(next, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('open')]);

  // ─── Deep-link: /email?compose=1&to=…&cc=…&subject=… opens Compose ──
  // Used by the Accounts contact panel email icon (and other shortcut
  // buttons) to jump straight into a pre-filled Compose modal.
  const [composePrefill, setComposePrefill] = useState(null);
  useEffect(() => {
    if (searchParams.get('compose') !== '1') return;
    const to = searchParams.get('to') || '';
    const cc = searchParams.get('cc') || '';
    const subject = searchParams.get('subject') || '';
    const body = searchParams.get('body') || '';
    setComposePrefill({
      to: to ? [to] : [],
      cc: cc ? [cc] : [],
      subject,
      body,
    });
    setComposeMode('new');
    setShowCompose(true);
    // Consume the params so refresh/back doesn't re-open the modal.
    const next = new URLSearchParams(searchParams);
    next.delete('compose');
    next.delete('to');
    next.delete('cc');
    next.delete('subject');
    next.delete('body');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('compose')]);


  const [microsoftConnected, setMicrosoftConnected] = useState(null); // null = loading, true/false = status
  const [ssoLogin, setSsoLogin] = useState(false);

  // Check Microsoft connection on mount
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await axios.get(`${API}/auth/microsoft/connection-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMicrosoftConnected(response.data.connected);
        setSsoLogin(response.data.sso_login || false);
      } catch (error) {
        console.error('Error checking Microsoft connection:', error);
        setMicrosoftConnected(false);
      }
    };
    checkConnection();
  }, [token]);

  // Real-time auto-sync effect - only when Microsoft is connected
  useEffect(() => {
    if (autoSyncEnabled && folder !== 'drafts' && microsoftConnected) {
      // Immediate sync on enable
      const doSync = async () => {
        try {
          const response = await axios.post(`${API}/email/sync`, { folder, force_refresh: false }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (!response.data.skipped) {
            // Wait for background sync to complete, then refresh
            setTimeout(() => {
              fetchEmails();
              fetchStats();
            }, 2500);
          }
          setLastSyncTime(new Date());
        } catch (error) {
          console.error('Auto-sync error:', error);
          if (error.response?.status === 400) {
            setMicrosoftConnected(false);
          }
        }
      };
      
      // Sync immediately
      doSync();
      
      // Then sync every 30 seconds
      syncIntervalRef.current = setInterval(doSync, 30000);

      return () => {
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
        }
      };
    } else {
      // Clear interval when auto-sync disabled
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    }
  }, [autoSyncEnabled, folder, token, fetchEmails, microsoftConnected]);

  // Real-time push via WebSocket — refresh inbox when Graph says "you have new mail".
  // Runs alongside the 30s polling above (polling remains the fallback).
  const { isConnected: wsConnected } = useRealtimeNotifications((evt) => {
    if (!evt || evt.type !== 'graph_change') return;
    if (evt.kind === 'mail') {
      // A mail was created/updated/deleted — pull the latest inbox slice
      fetchEmails();
      fetchStats();
      setLastSyncTime(new Date());
    }
  });

  // Set initial sync timestamp
  useEffect(() => {
    if (autoSyncEnabled && microsoftConnected) {
      setLastSyncTime(new Date());
    }
  }, [autoSyncEnabled, microsoftConnected]);

  const handleSync = async (forceRefresh = false) => {
    if (!microsoftConnected) {
      showMessage('error', 'Please connect Microsoft 365 first to sync emails. Click "Connect Now" above or open Calendar panel.');
      setCalendarPanelOpen(true);
      return;
    }
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/email/sync`, { folder, force_refresh: forceRefresh }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.skipped) {
        showMessage('success', 'Emails are up to date.');
        setSyncing(false);
      } else {
        showMessage('success', 'Syncing emails from Microsoft 365...');
        // Poll for sync completion — refresh after delay
        setTimeout(async () => {
          await fetchEmails();
          await fetchStats();
          setSyncing(false);
          setLastSyncTime(new Date());
          showMessage('success', 'Email sync complete!');
        }, 3000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Sync failed';
      showMessage('error', errorMsg);
      if (error.response?.status === 400) {
        setMicrosoftConnected(false);
      }
      setSyncing(false);
    }
  };

  const handleSelectEmail = async (email) => {
    try {
      const response = await axios.get(`${API}/email/${email.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEmail(response.data);
      // Update local state to mark as read + sync lead_id (auto-cleaned if lead was deleted)
      setEmails(prev => prev.map(e => e.id === email.id ? { ...e, is_read: true, lead_id: response.data.lead_id || null } : e));
    } catch (error) {
      showMessage('error', 'Failed to load email');
    }
  };

  const handleEmailAction = async (action, emailIds) => {
    try {
      await axios.post(`${API}/email/action`, {
        action,
        email_ids: Array.isArray(emailIds) ? emailIds : [emailIds]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEmails();
      if (action === 'delete' || action === 'archive') {
        setSelectedEmail(null);
      }
    } catch (error) {
      showMessage('error', 'Action failed');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-IN', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    }
  };

  // ─── Inbox helpers (state-mutating; pure helpers live in InboxList.js) ───
  // Selection helpers
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectVisible = (visibleIds) => {
    setSelectedIds(new Set(visibleIds));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const toggleExpand = (key) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const runBulk = async (action) => {
    if (selectedIds.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/email/action`, {
        action,
        email_ids: Array.from(selectedIds),
      }, { headers: { Authorization: `Bearer ${token}` } });
      const count = selectedIds.size;
      clearSelection();
      await fetchEmails();
      await fetchStats();
      const labelMap = { mark_read: 'marked as read', mark_unread: 'marked as unread', archive: 'archived', delete: 'deleted', star: 'starred' };
      showMessage('success', `${count} email${count > 1 ? 's' : ''} ${labelMap[action] || action}`);
    } catch (err) {
      showMessage('error', err.response?.data?.detail || `Bulk ${action} failed`);
    } finally {
      setBulkBusy(false);
    }
  };
  // ──────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="email-page">
      {/* Microsoft Connection Warning - Only show for non-SSO users who aren't connected */}
      {microsoftConnected === false && !ssoLogin && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-sm text-amber-800 dark:text-amber-300">
              <strong>Microsoft 365 not connected.</strong> Connect your account to sync emails, calendar and tasks.
            </span>
          </div>
          <button
            onClick={() => setCalendarPanelOpen(true)}
            className="px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition"
            data-testid="connect-microsoft-btn"
          >
            Connect Now
          </button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Email</h1>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            <span>{stats?.inbox?.unread || 0} unread • {stats?.inbox?.total || 0} total</span>
            {microsoftConnected && autoSyncEnabled && lastSyncTime && (
              <span className="hidden sm:flex items-center gap-1 text-green-600 dark:text-green-400">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Auto-sync on • Last: {lastSyncTime.toLocaleTimeString()}
              </span>
            )}
            {wsConnected && (
              <span
                className="hidden sm:flex items-center gap-1 text-blue-600 dark:text-blue-400"
                title="Real-time push via Microsoft Graph webhook"
                data-testid="ws-live-indicator"
              >
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                Live
              </span>
            )}
            {microsoftConnected && !autoSyncEnabled && (
              <span className="hidden sm:flex items-center gap-1 text-slate-400 dark:text-slate-500">
                <span className="w-2 h-2 bg-slate-400 rounded-full"></span>
                Auto-sync off
              </span>
            )}
            {!microsoftConnected && (
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                </svg>
                <span className="hidden sm:inline">Sync paused — </span>Microsoft not connected
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Auto-sync toggle - hidden on mobile */}
          <label className="hidden sm:flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
            <input
              type="checkbox"
              checked={autoSyncEnabled}
              onChange={(e) => setAutoSyncEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
            />
            Auto-sync
          </label>
          
          {/* Calendar toggle */}
          <button
            onClick={() => setCalendarPanelOpen(!calendarPanelOpen)}
            className={`btn-secondary flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm ${calendarPanelOpen ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700' : ''}`}
            data-testid="toggle-calendar-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="hidden sm:inline">{calendarPanelOpen ? 'Hide Calendar' : 'Calendar'}</span>
          </button>
          
          <button
            onClick={() => handleSync(true)}
            disabled={syncing}
            className="btn-secondary flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm"
            data-testid="sync-btn"
          >
            <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">{syncing ? 'Syncing...' : 'Sync'}</span>
          </button>
          <button
            onClick={() => { setComposeMode('new'); setShowCompose(true); }}
            className="btn-primary flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm"
            data-testid="compose-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Compose
          </button>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-4 p-3 rounded-lg ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'}`}>
          {message.text}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row gap-4 overflow-hidden">
        {/* Sidebar - Folders (Hidden on mobile, show as horizontal scroll on tablet) */}
        <div className="hidden md:block w-48 xl:w-56 2xl:w-60 flex-shrink-0">
          <div className="card p-3 space-y-1">
            <button
              onClick={() => { setFolder('inbox'); setSelectedEmail(null); }}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition ${
                folder === 'inbox' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                Inbox
              </span>
              {stats?.inbox?.unread > 0 && (
                <span className="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs px-2 py-0.5 rounded-full">{stats.inbox.unread}</span>
              )}
            </button>
            <button
              onClick={() => { setFolder('sent'); setSelectedEmail(null); }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                folder === 'sent' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Sent
            </button>
            <button
              onClick={() => { setFolder('all'); setSelectedEmail(null); }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                folder === 'all' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              All Mail
            </button>
            <button
              onClick={() => { setFolder('drafts'); setSelectedEmail(null); }}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition ${
                folder === 'drafts' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
              data-testid="drafts-folder-btn"
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Drafts
              </span>
              {draftsCount > 0 && (
                <span className="bg-slate-200 text-slate-700 dark:text-slate-300 text-xs px-2 py-0.5 rounded-full">{draftsCount}</span>
              )}
            </button>
            <button
              onClick={() => { setFolder('snoozed'); setSelectedEmail(null); }}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition ${
                folder === 'snoozed' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
              data-testid="snoozed-folder-btn"
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Snoozed
              </span>
            </button>
            
            <div className="border-t border-slate-200 dark:border-slate-700 my-2"></div>
            
            <div className="px-3 py-2 text-xs text-slate-400 uppercase tracking-wider">Quick Stats</div>
            <div className="px-3 py-1 text-sm text-slate-600 dark:text-slate-400">
              <span className="font-medium">{stats?.starred || 0}</span> Starred
            </div>
            <div className="px-3 py-1 text-sm text-slate-600 dark:text-slate-400">
              <span className="font-medium">{stats?.linked_to_leads || 0}</span> Linked to Leads
            </div>
          </div>
        </div>

        {/* Mobile Folder Selector - Horizontal Scroll on Mobile */}
        <div className="md:hidden w-full flex-shrink-0 mb-2">
          <div className="flex gap-2 overflow-x-auto pb-2 px-1 scrollbar-hide">
            <button
              onClick={() => { setFolder('inbox'); setSelectedEmail(null); }}
              className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                folder === 'inbox' ? 'bg-slate-900 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              Inbox {stats?.inbox?.unread > 0 && `(${stats.inbox.unread})`}
            </button>
            <button
              onClick={() => { setFolder('sent'); setSelectedEmail(null); }}
              className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                folder === 'sent' ? 'bg-slate-900 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Sent
            </button>
            <button
              onClick={() => { setFolder('all'); setSelectedEmail(null); }}
              className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                folder === 'all' ? 'bg-slate-900 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              All Mail
            </button>
            <button
              onClick={() => { setFolder('drafts'); setSelectedEmail(null); }}
              className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                folder === 'drafts' ? 'bg-slate-900 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Drafts {draftsCount > 0 && `(${draftsCount})`}
            </button>
          </div>
        </div>

        {/* Email List */}
        <div className={`${selectedEmail ? 'hidden md:flex' : 'flex'} w-full md:w-80 lg:w-96 xl:w-[28rem] 2xl:w-[32rem] flex-shrink-0 card overflow-hidden flex-col`}>
          {/* Search */}
          <div className="p-3 border-b border-slate-200 dark:border-slate-700">
            <div className="relative">
              <input
                type="text"
                placeholder="Search emails..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="email-search"
              />
              <svg className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Quick filters + saved searches (Batch 3) */}
          {folder !== 'drafts' && (
            <QuickFilters
              quickFilter={quickFilter}
              setQuickFilter={setQuickFilter}
              search={search}
              setSearch={setSearch}
            />
          )}

          {/* Email List */}
          <div className="flex-1 overflow-y-auto">
            {/* Drafts folder view */}
            {folder === 'drafts' ? (
              <DraftsList
                drafts={drafts}
                selectedDraftId={selectedDraft?.id}
                formatDate={formatDate}
                onEdit={(draft) => {
                  setSelectedDraft(draft);
                  setComposeMode('new');
                  setShowCompose(true);
                }}
                onDelete={async (draftId) => {
                  if (!window.confirm('Delete this draft?')) return;
                  try {
                    await axios.delete(`${API}/email/drafts/${draftId}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    fetchDrafts();
                  } catch (error) {
                    console.error('Error deleting draft:', error);
                  }
                }}
              />
            ) : (() => {
              const filtered = applyQuickFilter(Array.isArray(emails) ? emails : [], quickFilter);
              if (filtered.length === 0) {
                return (
                  <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                    <svg className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p className="font-medium">{quickFilter !== 'all' ? `No ${quickFilter} emails` : 'No emails found'}</p>
                    {quickFilter !== 'all' ? (
                      <button onClick={() => setQuickFilter('all')} className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline">
                        Clear filter
                      </button>
                    ) : microsoftConnected ? (
                      <button onClick={() => handleSync(true)} className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline" data-testid="sync-empty-btn">
                        Click to sync from Microsoft 365
                      </button>
                    ) : (
                      <p className="text-sm mt-1">Connect Microsoft 365 to sync your inbox</p>
                    )}
                  </div>
                );
              }
              return (
                <InboxList
                  emails={filtered}
                  visibleCount={visibleCount}
                  setVisibleCount={setVisibleCount}
                  selectedEmail={selectedEmail}
                  selectedIds={selectedIds}
                  toggleSelect={toggleSelect}
                  selectVisible={selectVisible}
                  clearSelection={clearSelection}
                  threadingEnabled={threadingEnabled}
                  setThreadingEnabled={setThreadingEnabled}
                  expandedThreads={expandedThreads}
                  toggleExpand={toggleExpand}
                  bulkBusy={bulkBusy}
                  runBulk={runBulk}
                  folder={folder}
                  formatDate={formatDate}
                  handleSelectEmail={handleSelectEmail}
                />
              );
            })()}
          </div>
        </div>

        {/* Email Detail */}
        <div className={`${selectedEmail ? 'flex' : 'hidden md:flex'} flex-1 card overflow-hidden flex-col`}>
          {selectedEmail ? (
            <>
              {/* Mobile Back Button */}
              <button 
                onClick={() => setSelectedEmail(null)}
                className="md:hidden flex items-center gap-2 p-3 border-b border-slate-200 dark:border-slate-700 text-blue-600 font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to emails
              </button>
              
              {/* Email Header */}
              <div className="p-3 sm:p-4 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white break-words">{selectedEmail.subject}</h2>
                    <div className="mt-2 flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center text-white font-semibold text-sm sm:text-base flex-shrink-0">
                        {(selectedEmail.from_name || selectedEmail.from_email || '?')[0].toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 dark:text-white text-sm sm:text-base truncate">{selectedEmail.from_name || selectedEmail.from_email}</p>
                        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                          To: {selectedEmail.to_recipients?.join(', ')}
                          {selectedEmail.cc_recipients?.length > 0 && ` • CC: ${selectedEmail.cc_recipients.join(', ')}`}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs sm:text-sm text-slate-400 whitespace-nowrap">{formatDate(selectedEmail.received_at || selectedEmail.sent_at || selectedEmail.synced_at || selectedEmail.created_at)}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-3 sm:mt-4 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => { setComposeMode('reply'); setShowCompose(true); }}
                    className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1.5"
                    data-testid="reply-btn"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    Reply
                  </button>
                  <button
                    onClick={() => { setComposeMode('reply_all'); setShowCompose(true); }}
                    className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1.5"
                    data-testid="reply-all-btn"
                    title="Reply to sender + everyone on To/CC"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16H3v-4m0 0l4-4m-4 4l4 4m10-8h4v4m0 0l-4 4m4-4l-4-4" />
                    </svg>
                    Reply All
                  </button>
                  <button
                    onClick={() => { setComposeMode('forward'); setShowCompose(true); }}
                    className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1.5"
                    data-testid="forward-btn"
                    title="Forward this email (attachments included)"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8a5 5 0 00-5 5v1" />
                    </svg>
                    Forward
                  </button>
                  <SnoozeFollowupActions
                    email={selectedEmail}
                    token={token}
                    onChange={async () => {
                      // Refresh and re-resolve current selection so the inline
                      // snooze/follow-up state badge updates without losing context.
                      const prevId = selectedEmail?.id;
                      const refreshed = await fetchEmails();
                      const list = Array.isArray(refreshed) ? refreshed : [];
                      const next = prevId ? list.find((e) => e.id === prevId) : null;
                      setSelectedEmail(next || null);
                    }}
                  />
                  <button
                    onClick={() => handleEmailAction('star', selectedEmail.id)}
                    className={`btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1.5 ${selectedEmail.starred ? 'text-yellow-600' : ''}`}
                  >
                    <svg className="w-4 h-4 mr-1" fill={selectedEmail.starred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    Star
                  </button>
                  <button
                    onClick={() => handleEmailAction('archive', selectedEmail.id)}
                    className="btn-secondary text-sm"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    Archive
                  </button>
                  <button
                    onClick={() => handleEmailAction('delete', selectedEmail.id)}
                    className="btn-secondary text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                  
                  <div className="border-l border-slate-200 dark:border-slate-700 h-6 mx-2"></div>
                  
                  {/* CRM Actions */}
                  {!selectedEmail.lead_id ? (
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="btn-primary text-sm"
                      data-testid="create-from-email-btn"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create Lead
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg text-sm">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Linked: {selectedEmail.lead?.name || 'Lead'}
                    </span>
                  )}
                  
                  {/* Create Task Button */}
                  <button
                    onClick={() => setShowCreateTaskModal(true)}
                    className="btn-secondary text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 border-amber-300 dark:border-amber-700"
                    data-testid="create-task-from-email-btn"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Create Task
                  </button>

                  {/* Create Opportunity Button */}
                  {selectedEmail.lead_id ? (
                    <button
                      onClick={() => setShowCreateOppModal(true)}
                      className="btn-secondary text-sm text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 border-purple-300 dark:border-purple-700"
                      data-testid="create-opp-from-email-btn"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      + Opportunity
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-400 dark:text-slate-500" title="Link a lead first to create an opportunity">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Link lead for Opp
                    </span>
                  )}
                </div>

                {/* Attachments */}
                {selectedEmail.attachments?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {selectedEmail.attachments.map((att, idx) => (
                      <button
                        key={att.id || att.name || idx}
                        onClick={async () => {
                          try {
                            const response = await axios.get(`${API}/email/${selectedEmail.id}/attachments/${att.id}`, {
                              headers: { Authorization: `Bearer ${token}` },
                              responseType: 'blob'
                            });
                            const url = window.URL.createObjectURL(response.data);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = att.name || 'attachment';
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } catch (err) {
                            showMessage('error', 'Failed to download attachment. Microsoft 365 may need to be reconnected.');
                          }
                        }}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-300 transition cursor-pointer"
                        data-testid={`attachment-${idx}`}
                      >
                        <svg className="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {att.name}
                        <span className="text-xs text-slate-400">({Math.round((att.size || 0) / 1024)}KB)</span>
                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Email Body — Outlook-style conversation view */}
              <div className="flex-1 overflow-y-auto p-4">
                {selectedEmail.thread?.length > 0 ? (
                  <ConversationThread
                    current={selectedEmail}
                    thread={selectedEmail.thread}
                    formatDate={formatDate}
                  />
                ) : (
                  <div
                    className="prose prose-sm max-w-none dark:prose-invert email-body-container bg-white dark:bg-slate-800 rounded-lg p-4"
                    style={{ colorScheme: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: selectedEmail.body_html || `<p>${selectedEmail.body_preview || 'No content'}</p>` }}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="font-medium">Select an email to read</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Calendar Panel */}
        {calendarPanelOpen && (
          <EmailCalendarPanel 
            isOpen={calendarPanelOpen} 
            onToggle={() => setCalendarPanelOpen(!calendarPanelOpen)} 
          />
        )}
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <ComposeModal
          mode={composeMode}
          replyTo={['reply', 'reply_all', 'forward'].includes(composeMode) ? selectedEmail : null}
          leadId={selectedEmail?.lead_id}
          onClose={() => {
            setShowCompose(false);
            setSelectedDraft(null);
            setComposePrefill(null);
          }}
          onSent={() => {
            setShowCompose(false);
            setSelectedDraft(null);
            setComposePrefill(null);
            fetchEmails();
            fetchDrafts();
            // Re-fetch the currently-selected email so its `thread` array
            // includes the reply we just sent. Without this, the right-pane
            // keeps showing the pre-send thread state — making the user think
            // their reply "disappeared" or didn't thread.
            if (selectedEmail?.id) {
              axios.get(`${API}/email/${selectedEmail.id}`, {
                headers: { Authorization: `Bearer ${token}` },
              }).then((r) => {
                if (r.data) setSelectedEmail(r.data);
              }).catch(() => {});
            }
            showMessage('success', 'Email sent successfully!');
          }}
          token={token}
          currentUserEmail={user?.email}
          draftId={selectedDraft?.id}
          initialData={selectedDraft ? {
            to: selectedDraft.to || [],
            cc: selectedDraft.cc || [],
            subject: selectedDraft.subject || '',
            body: selectedDraft.body || ''
          } : composePrefill}
        />
      )}

      {/* Create from Email Modal */}
      {showCreateModal && selectedEmail && (
        <CreateFromEmailModal
          email={selectedEmail}
          onClose={() => setShowCreateModal(false)}
          onCreated={(result) => {
            setShowCreateModal(false);
            handleSelectEmail(selectedEmail); // Refresh email to show linked lead
            showMessage('success', 'Lead created and linked to email!');
          }}
          token={token}
        />
      )}
      
      {/* Create Task from Email Modal */}
      {showCreateTaskModal && selectedEmail && (
        <CreateTaskFromEmailModal
          isOpen={showCreateTaskModal}
          email={selectedEmail}
          onClose={() => setShowCreateTaskModal(false)}
          onTaskCreated={(result) => {
            showMessage('success', `Task created${result.auto_detected_company ? ` (Company: ${result.auto_detected_company})` : ''}!`);
          }}
        />
      )}

      {/* Create Opportunity from Email Modal */}
      {showCreateOppModal && selectedEmail && selectedEmail.lead_id && (
        <CreateOppFromEmailModal
          email={selectedEmail}
          onClose={() => setShowCreateOppModal(false)}
          onCreated={() => {
            setShowCreateOppModal(false);
            showMessage('success', 'Opportunity created from email!');
          }}
        />
      )}
    </div>
  );
}

// Compose Email Modal Component with Autocomplete and Drafts

export default function EmailPageWithBoundary() {
  return (
    <ErrorBoundary title="Email page crashed">
      <EmailPage />
    </ErrorBoundary>
  );
}
